import { useEffect, useState } from "react";
import CustomBlockButton from "../../../../../../../components/button/CustomBlockButton";
import { CustomDialog } from "../../../../../../../components/dialog/v1/CustomDialog";
import styled from 'styled-components';
import moment from "moment";
import { CustomDateUtils } from "../../../../../../../utils/CustomDateUtils";
import { GlobalWegetDateSelectorModal } from "../../../../../../../global-wegets/global-weget-date-selector-modal";
import { v4 as uuidv4 } from 'uuid';
import { CustomLabelInput } from "../../../../../../../components/input/CustomLabelInput";
import { useShoppingCartStagesActionsContextHook, useShoppingCartStagesValueContextHook } from "../../../../contexts/ShoppingCartStagesContext";
import { customToast } from "../../../../../../../components/toast/custom-react-toastify/v1";
import { LucideSquarePen, LucideTrash2 } from "lucide-react";
import { useCreateShoppingCartStagesMutation, useDeleteShoppingCartStagesMutation, useShoppingCartStagesQuery, useUpdateShoppingCartStagesMutation } from "../../../../Data/shoppingCartStagesReactQuery/ShoppingCartStagesReactQuery";
import { useSelectedShoppingCartStageActionsContextHook, useSelectedShoppingCartStageValueContextHook } from "../../../../contexts/SelectedShoppingCartStageContext";

const StyledPaper = styled.div`
    background: var(--backgroundField);
`;

const StyledViewModeDefaultBody = styled.div`
    padding: 10px;
    .add-stage-box{
        margin-top: 20px;
        button{
            background: #333;
            color: #fff;
            font-weight: 600;
            border-radius: 3px;
            border: none;
        }
    }

    .date-range-box{
        
        width: 100%;
        input{
            border-color: #e0e0e0;
            border-radius: 3px;

            &:focus{
                border-color: #333333;
                box-shadow: 0 0 0 0.2rem rgba(0, 0, 0, 0.25);
            }
        }

        button{
            border-radius: 3px;
            border-color: #333;
            font-weight: 600;
            color: #333;
        }
    }

    .stage-list-box{
        margin-top: 20px;
        display: flex;
        flex-direction: column;
        gap: 10px;
        .stage-item{
            display: flex;
            justify-content: space-between;
            align-items: center;
            background: #fff;
            border: 1px solid #e0e0e0;
            padding: 10px;
            border-radius: 3px;
            min-height: 40px;
            transition: all 0.3s;

            &:hover{
                box-shadow: var(--shadow-sm);
                scale: 1.02;
                cursor: pointer;
                border-radius: 10px;
            }

            &.isDeleteMode{
                justify-content: flex-end;
                gap: 10px;
                scale: 1.02;
                box-shadow: var(--shadow-sm);
                button{
                    height: 30px;
                    width: 80px;
                    border-radius: 5px;

                    &.delete{
                        background: var(--destructive);
                        border: 1px solid var(--destructive);
                        font-weight: 600;
                        color: var(--destructive-foreground);
                    }

                    &.cancel{
                        background: var(--cancel);
                        border: 1px solid var(--cancel);
                        font-weight: 600;
                        color: var(--cancel-foreground);
                    }
                }
            }

            &.isEditMode{
                display: flex;
                justify-content: flex-start;
                gap: 10px;

                button{
                    height: 30px;
                    width: 80px;
                    border-radius: 5px;
                    background: var(--primary);
                    border: 1px solid var(--primary);
                    font-weight: 600;
                    color: var(--primary-foreground);
                }

                .input-box{
                    flex:1;
                    input{
                        border-color: #efefef;
                        border-radius: 3px;

                        &:focus{
                            border-color: #333333;
                        }
                    }
                }
            }

            .handlers-box{
                display: flex;
                gap: 10px;
            }

        }
    }
`;

const StyledViewModeAddBody = styled.form`
    padding: 10px;

    input{
        border: 1px solid #e0e0e0;
        border-radius: 3px;
        background: #fff;
    }
    button{
        margin-top: 20px;
        background: #333;
        color: #fff;
        font-weight: 600;
        border-radius: 3px;
        border: none;
    }
`;

const CREATE_SHOPPING_CART_STAGE_FORM = {
    uuid: null,
    name: '',
    stageDate: null
}

export function ModalShoppingCartStages({
    open,
    onClose
}) {
    const [viewMode, setViewMode] = useState('DEFAULT'); // DEFAULT, ADD, EDIT, DELETE

    const [dateRange, setDateRange] = useState(() => {
        const currentDateTimeMoment = moment();
        const startDate = CustomDateUtils().dateToYYYYMMDD(currentDateTimeMoment);
        const endDate = CustomDateUtils().dateToYYYYMMDD(currentDateTimeMoment);
        return {
            startDate: startDate,
            endDate: endDate
        }
    })
    const [dateRangeSelectorOpen, setDateRangeSelectorOpen] = useState(false);

    return (
        <>
            <CustomDialog
                open={open}
                onClose={onClose}
            >
                <StyledPaper>
                    {viewMode === 'DEFAULT' &&
                        <ViewModeDefault
                            dateRange={dateRange}
                            onClose={onClose}
                            onSetViewMode={setViewMode}
                            onSetDateRangeSelectorOpen={setDateRangeSelectorOpen}
                        />
                    }
                    {viewMode === 'ADD' &&
                        <ViewModeAdd
                            onClose={onClose}
                            onSetViewMode={setViewMode}
                        />
                    }
                </StyledPaper>
            </CustomDialog>

            {dateRangeSelectorOpen &&
                <GlobalWegetDateSelectorModal
                    open={dateRangeSelectorOpen}
                    onClose={() => setDateRangeSelectorOpen(false)}
                    initialStartDateTime={new Date(dateRange?.startDate)}
                    initialEndDateTime={new Date(dateRange?.endDate)}
                    onAccept={(val) => {
                        setDateRange(prev => {
                            return {
                                ...prev,
                                startDate: val?.startDate,
                                endDate: val?.endDate
                            }
                        });
                        setDateRangeSelectorOpen(false)
                    }}
                />
            }
        </>
    );
}

function ViewModeDefault({
    dateRange,
    onClose,
    onSetViewMode,
    onSetDateRangeSelectorOpen,
}) {
    const shoppingCartStagesContextValueHook = useShoppingCartStagesValueContextHook();
    const shoppingCartStagesContextActionsHook = useShoppingCartStagesActionsContextHook();
    const shoppingCartStages = shoppingCartStagesContextValueHook.shoppingCartStages;

    const selectedShoppingCartStageContextActionsHook = useSelectedShoppingCartStageActionsContextHook();

    const { data } = useShoppingCartStagesQuery({
        dateRange: dateRange,
    })
    const deleteShoppingCartStagesMutation = useDeleteShoppingCartStagesMutation();
    const updateShoppingCartStagesMutation = useUpdateShoppingCartStagesMutation();

    const [deleteTargetUuid, setDeleteTargetUuid] = useState(null);
    const [editTargetStage, setEditTargetStage] = useState(null);

    useEffect(() => {
        if (data?.data?.content) {
            shoppingCartStagesContextActionsHook.shoppingCartStages.onSet(data?.data?.content);
        }
    }, [data]);

    const handleDeleteStage = (stage) => {
        console.log(stage);
        deleteShoppingCartStagesMutation.mutate({
            uuid: stage?.uuid
        }, {
            onSuccess: () => {
                setDeleteTargetUuid(null);
            }
        })
    }

    const handleEditStage = (originalStage) => {

        if (originalStage?.name === editTargetStage?.name) {
            setEditTargetStage(null);
            return;
        }

        // name이 비어있거나 30자 이상이면 취소
        if (!editTargetStage.name || editTargetStage.name.length > 30) {
            customToast.error('스테이지명을 입력해주세요 (최대 30자)');
            return;
        }

        updateShoppingCartStagesMutation.mutate({
            body: {
                payload: {
                    ...editTargetStage,
                }
            }
        }, {
            onSuccess: () => {
                onSetViewMode('DEFAULT');
                setEditTargetStage(null);
            }
        });
    }

    const handleSelectStage = (stage) => {
        selectedShoppingCartStageContextActionsHook.selectedShoppingCartStage.onSet(stage);
        onClose();
    }
    return (
        <>
            <CustomDialog.Header style={{ padding: 10 }}>
                <CustomDialog.Header.Fake></CustomDialog.Header.Fake>
                <CustomDialog.Header.Title>장바구니 스테이지</CustomDialog.Header.Title>
                <CustomDialog.Header.Close onClick={() => onClose()}></CustomDialog.Header.Close>
            </CustomDialog.Header>
            <StyledViewModeDefaultBody>
                <div className='date-range-box'>
                    <CustomBlockButton
                        type='button'
                        onClick={() => onSetDateRangeSelectorOpen(true)}
                    >
                        <div style={{ fontSize: '11px', marginBottom: '5px', color: '#606060' }}>조회 기간</div>
                        <div>
                            {CustomDateUtils().dateToYYYYMMDD(dateRange?.startDate)} ~ {CustomDateUtils().dateToYYYYMMDD(dateRange?.endDate)}
                        </div>
                    </CustomBlockButton>
                </div>
                <div className='add-stage-box'>
                    <CustomBlockButton
                        type='button'
                        onClick={() => onSetViewMode('ADD')}
                    >
                        스테이지 추가
                    </CustomBlockButton>
                </div>
                <div className='stage-list-box'>
                    {shoppingCartStages?.map(shoppingCartStage => {
                        const isDeleteMode = deleteTargetUuid && deleteTargetUuid === shoppingCartStage?.uuid;
                        const isEditMode = editTargetStage && editTargetStage?.uuid === shoppingCartStage?.uuid;

                        if (isDeleteMode) {
                            return (
                                <div key={shoppingCartStage?.uuid} className='stage-item isDeleteMode'>
                                    <CustomBlockButton
                                        type='button'
                                        className='delete'
                                        onClick={() => handleDeleteStage(shoppingCartStage)}
                                        autoFocus
                                    >
                                        삭제
                                    </CustomBlockButton>
                                    <CustomBlockButton
                                        type='button'
                                        className='cancel'
                                        onClick={() => setDeleteTargetUuid(null)}
                                    >
                                        취소
                                    </CustomBlockButton>
                                </div>
                            );
                        }

                        if (isEditMode) {
                            return (
                                <div key={shoppingCartStage?.uuid} className='stage-item isEditMode'>
                                    <CustomLabelInput className='input-box'>
                                        <CustomLabelInput.Input
                                            type='text'
                                            value={editTargetStage?.name || ''}
                                            onChange={(e) => {
                                                setEditTargetStage(prev => ({
                                                    ...prev,
                                                    name: e.target.value
                                                }));
                                            }}
                                            onKeyDown={() => {
                                                if (window.event.keyCode === 13) {
                                                    handleEditStage(shoppingCartStage);
                                                }
                                            }}
                                            autoFocus
                                        />
                                        <CustomLabelInput.Label>스테이지명</CustomLabelInput.Label>
                                    </CustomLabelInput>
                                    <CustomBlockButton
                                        type='button'
                                        onClick={() => handleEditStage(shoppingCartStage)}
                                    >완료</CustomBlockButton>
                                </div>
                            );
                        }

                        return (
                            <div key={shoppingCartStage?.uuid} className='stage-item' onClick={() => handleSelectStage(shoppingCartStage)}>
                                <div style={{ display: 'flex', flexDirection: 'row', gap: '10px', alignItems: 'center' }}>
                                    <div style={{ fontSize: '18px', fontWeight: '700' }}>{shoppingCartStage?.name}</div>
                                    <div style={{ fontSize: '11px', color: '#808080' }}>({shoppingCartStage?.stageDate})</div>
                                </div>
                                <div className='handlers-box'>
                                    <LucideSquarePen
                                        cursor={'pointer'}
                                        size={18}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setEditTargetStage(shoppingCartStage)
                                        }}
                                    />
                                    <LucideTrash2
                                        color={'var(--redColor1)'}
                                        cursor={'pointer'}
                                        size={18}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setDeleteTargetUuid(shoppingCartStage?.uuid)
                                        }}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </StyledViewModeDefaultBody>
        </>
    );
}

function ViewModeAdd({
    onClose,
    onSetViewMode,
}) {
    const [shoppingCartStageForm, setShoppingCartStageForm] = useState({
        ...CREATE_SHOPPING_CART_STAGE_FORM,
        uuid: uuidv4(),
        stageDate: moment().format('YYYY-MM-DD')
    });

    const shoppingCartStagesMutation = useCreateShoppingCartStagesMutation()

    const handleSubmit = async () => {
        // name이 비어있거나 30자 이상이면 취소
        if (!shoppingCartStageForm.name || shoppingCartStageForm.name.length > 30) {
            customToast.error('스테이지명을 입력해주세요 (최대 30자)');
            return;
        }

        shoppingCartStagesMutation.mutate({
            body: {
                payload: [
                    {
                        ...shoppingCartStageForm,
                        name: shoppingCartStageForm.name.trim(),
                        stageDate: shoppingCartStageForm.stageDate,
                    }
                ]
            }
        }, {
            onSuccess: () => {
                onSetViewMode('DEFAULT');
            }
        });
    }



    return (
        <>
            <CustomDialog.Header style={{ padding: 10 }}>
                <CustomDialog.Header.Back onClick={() => { onSetViewMode('DEFAULT') }}></CustomDialog.Header.Back>
                <CustomDialog.Header.Title>장바구니 스테이지 추가</CustomDialog.Header.Title>
                <CustomDialog.Header.Close onClick={() => onClose()}></CustomDialog.Header.Close>
            </CustomDialog.Header>
            <StyledViewModeAddBody onSubmit={(e) => {
                e.preventDefault();
                handleSubmit();
            }}>
                <CustomLabelInput>
                    <CustomLabelInput.Input
                        type='text'
                        value={shoppingCartStageForm.name || ''}
                        onChange={(e) => {
                            setShoppingCartStageForm(prev => ({
                                ...prev,
                                name: e.target.value
                            }));
                        }}
                        autoFocus
                    />
                    <CustomLabelInput.Label>스테이지명</CustomLabelInput.Label>
                </CustomLabelInput>
                <CustomBlockButton
                    type='submit'
                >
                    추가
                </CustomBlockButton>
            </StyledViewModeAddBody>
        </>
    );
}